import React, { useState, useEffect, useRef } from 'react';
import Head from 'next/head';
import Link from 'next/link';
import Image from 'next/image';
import dynamic from 'next/dynamic';
import SimilarIcons from '../components/SimilarIcons'; 

// Динамическая загрузка компонентов только на клиенте
const DownloadPNGButton = dynamic(() => import('../components/DownloadPNGButton'), { ssr: false });
const DownloadSVGButton = dynamic(() => import('../components/DownloadSVGButton'), { ssr: false });
const CopyPNGButton = dynamic(() => import('../components/CopyPNGButton'), { ssr: false });
const CopySVGButton = dynamic(() => import('../components/CopySVGButton'), { ssr: false });
const AddToCartButton = dynamic(() => import('../components/AddToCartButton'), { ssr: false });
const DownloadFreePNG = dynamic(() => import('../components/DownloadFreePNG'), { ssr: false });
const CopyFreePNG = dynamic(() => import('../components/CopyFreePNG'), { ssr: false });

/**
 * Helper function to format keywords
 */
const formatKeywords = (keywordsInput, count = 3) => {
  if (!keywordsInput) return '';

  let keywordsArray = [];
  if (Array.isArray(keywordsInput)) {
    keywordsArray = keywordsInput;
  } else if (typeof keywordsInput === 'string') {
    keywordsArray = keywordsInput.split(',').map((k) => k.trim());
  }

  return keywordsArray.filter((k) => k).slice(0, count).join(', ');
};

/**
 * Helper function to ensure title ends with "icon"
 */
const ensureIconSuffix = (title) => {
  if (!title) return 'icon';
  
  const lowerTitle = title.toLowerCase();
  
  // Check if already ends with icon, -icon, or _icon
  if (lowerTitle.endsWith('icon') || 
      lowerTitle.endsWith('-icon') || 
      lowerTitle.endsWith('_icon')) {
    return title;
  }
  
  return `${title} icon`;
};

// PNG Button Placeholder - matches DownloadPNGButton
const PNGButtonPlaceholder = () => (
  <div className="flex border border-black shadow-md rounded-md overflow-hidden min-h-[55px]">
    <button className="flex-1 px-3 py-1 bg-transparent text-black hover:bg-gray-100 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center">
      <span className="flex-1">Download PNG</span>
      <span className="ml-2 cursor-pointer inline-flex items-center justify-center rounded-full w-[30px] h-[30px] select-none transition-colors duration-200 bg-transparent">
        <svg 
          width="16" 
          height="16" 
          viewBox="0 0 12 12" 
          fill="none" 
          stroke="currentColor" 
          strokeWidth="1"
          strokeLinecap="round"
        >
          <path d="M3 4.5l3 3 3-3"/>
        </svg>
      </span>
    </button>
    <button className="px-2 py-3 hover:bg-gray-100 transition-colors flex items-center justify-center min-w-[45px] border-l border-black">
      <svg 
        width="20" 
        height="20" 
        viewBox="0 0 24 24" 
        fill="none" 
        stroke="currentColor" 
        strokeWidth="2"
      >
        <rect x="9" y="9" width="13" height="13" rx="2" ry="2"></rect>
        <path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2 2v1"></path>
      </svg>
    </button>
  </div>
);

// SVG Button Placeholder - matches DownloadSVGButton
const SVGButtonPlaceholder = () => (
  <div className="flex border border-black shadow-md rounded-md overflow-hidden min-h-[55px]">
    <button className="flex-1 px-3 py-3 bg-transparent text-black hover:bg-gray-100 transition-colors">
      Download SVG
    </button>
    <button className="px-2 py-3 hover:bg-gray-100 transition-colors flex items-center justify-center min-w-[45px] border-l border-black">
      <svg 
        width="20" 
        height="20" 
        viewBox="0 0 24 24" 
        fill="none" 
        stroke="currentColor" 
        strokeWidth="2"
      >
        <rect x="9" y="9" width="13" height="13" rx="2" ry="2"></rect>
        <path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2 2v1"></path>
      </svg>
    </button>
  </div>
);

// Add to Cart Button Placeholder - keep existing styling
const AddToCartButtonPlaceholder = ({ price }) => (
  <div className="relative min-h-[55px] overflow-hidden w-full">
    <button className="absolute w-full h-full text-base cursor-pointer border-none rounded-md text-white z-10 font-bold text-center bg-black flex justify-center items-center">
      <span className="inline-block">{`Add SVG to Cart ${price.toFixed(2)}`}</span>
    </button>
  </div>
);

// Download Free PNG Placeholder - matches DownloadFreePNG
const DownloadFreePNGPlaceholder = () => (
  <div className="flex border border-black shadow-md rounded-md overflow-hidden min-h-[55px]">
    <button className="flex-1 px-3 py-3 bg-transparent text-black hover:bg-gray-100 transition-colors">
      Download PNG for free
    </button>
    <button className="px-2 py-3 hover:bg-gray-100 transition-colors flex items-center justify-center min-w-[45px] border-l border-black">
      <svg 
        width="20" 
        height="20" 
        viewBox="0 0 24 24" 
        fill="none" 
        stroke="currentColor" 
        strokeWidth="2"
      >
        <rect x="9" y="9" width="13" height="13" rx="2" ry="2"></rect>
        <path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2 2v1"></path>
      </svg>
    </button>
  </div>
);

// Компонент, который отображает клиентский компонент только после гидратации
function ClientOnly({ children, placeholder }) {
  const [isClient, setIsClient] = useState(false);
  
  useEffect(() => {
    setIsClient(true);
  }, []);
  
  return isClient ? children : placeholder || null;
}

const IconDetail = ({
  slug,
  BACKEND_URL,
  collection,
  initialData,
  imageUrl: propImageUrl,
  accessStatus: initialAccessStatus,
  similarIconsData,
  similarCollections,
  authError = false,
  serverAuthState,
}) => {
  // Fallback data for missing initialData
  const defaultData = {
    keywords: slug ? [slug] : ['icon'],
    collection: collection || 'basic',
    id: null,
    media_type: collection === 'basic3d' ? 'rastericon' : 'icon',
    error_message: '',
    is_purchased: false,
    is_subscription_active: false,
    is_free: collection === 'basic' || collection === 'basic3d',
    image_url: propImageUrl || '',
  };

  if (!initialData) {
    return (
      <div className="p-8 text-center">
        <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-gray-900 mb-4"></div>
        <p className="text-xl">Loading icon details...</p>
      </div>
    );
  }

  const iconData = initialData
    ? {
        ...defaultData,
        ...initialData,
      }
    : defaultData;

  const {
    keywords: assetKeywords = '',
    collection: iconCollection = collection,
    id: assetId = null,
    media_type: mediaType = iconCollection === 'basic3d' ? 'rastericon' : 'icon',
    error_message: serverErrorMessage = '',
    is_purchased = false,
    is_subscription_active = false,
    is_free = iconCollection === 'basic' || iconCollection === 'basic3d',
  } = iconData;

  const hasAccess = is_free || is_purchased || is_subscription_active;
  // Приоритизируем url_large для basic3d
  const imageUrl =
    propImageUrl || 
    (iconCollection === 'basic3d' && iconData.url_large ? iconData.url_large : iconData.processedImageUrl || iconData.image_url || '');

  // Calculate price based on collection
  const price =
    iconCollection === 'basic' || iconCollection === 'basic3d'
      ? 0
      : iconCollection === 'outline' || iconCollection === 'polish-blue'
      ? 2
      : 1;

  // Флаг для отслеживания отправки события view_item
  const viewEventSentRef = useRef(false);

  useEffect(() => {
    // Отправляем событие просмотра иконки в dataLayer при монтировании компонента
    if (typeof window !== 'undefined' && window.dataLayer && !viewEventSentRef.current) {
      // ИСПРАВЛЕНО: Убраны дублирующие поля, оставлены только необходимые переменные в camelCase
      const eventData = {
        event: 'view_item',
        assetSlug: slug || '',
        assetCollection: iconCollection || '',
        assetPrice: price
      };
      
      // Добавляем категорию только если она есть у ассета
      if (iconData.category) {
        eventData.assetCategory = iconData.category;
      }
      
      window.dataLayer.push(eventData);
      
      // Отмечаем, что событие было отправлено
      viewEventSentRef.current = true;
    }
  }, [slug, iconCollection, iconData.category, price]);

  // Определяем, является ли скачивание бесплатным или платным
  const isFreeDownload = is_free && !is_purchased && !is_subscription_active;

  // Функция для отправки события скачивания с параметром action
  // ИСПРАВЛЕНО: Переименованы переменные на camelCase в соответствии с GTM настройками
  const handleDownload = (format, actionType = 'download') => {
    if (typeof window !== 'undefined' && window.dataLayer) {
      const timestamp = new Date().toISOString();
      
      const eventData = {
        event: 'download_asset',
        assetSlug: slug || '',
        downloadTimestamp: timestamp,
        userID: serverAuthState?.user?.id || '',
        userSubscriptionStatus: is_subscription_active ? 'active' : 'inactive',
        freeDownload: isFreeDownload,
        format: format,
        actionType: actionType
      };
      
      window.dataLayer.push(eventData);
    }
  };

  // Обработчики для разных типов скачивания
  const onPNGDownloadComplete = () => {
    handleDownload('png', 'download');
  };
  
  const onSVGDownloadComplete = () => {
    handleDownload('svg', 'download');
  };

  // Стандартный обработчик перед скачиванием
  const onBeforeDownload = () => true;
  
  // Обработчики для копирования
  const onBeforePNGCopy = () => {
    handleDownload('png', 'copy');
    return true;
  };
  
  const onBeforeSVGCopy = () => {
    handleDownload('svg', 'copy');
    return true;
  };

  const handleAddToCart = async () => {
    try {
      const cartStoreModule = await import('@/store/cartStore');
      const cartStore = cartStoreModule.default;
      const addToCart = cartStore.getState().addToCart;

      if (typeof addToCart !== 'function') {
        alert('Error adding to cart. Please try again.');
        return false;
      }

      // Добавляем товар в корзину без перенаправления
      await addToCart({
        id: assetId,
        assetId,
        product: { price },
        mediaType,
        slug,
        collection: iconCollection,
      });
      
      return true;
    } catch (error) {
      console.error("Error adding to cart:", error);
      alert('Error adding to cart. Please try again.');
      return false;
    }
  };

  // SEO and licensing helpers
  const rawTitle = formatKeywords(assetKeywords, 3) || slug || 'Icon';
  const derivedTitle = ensureIconSuffix(rawTitle);
  const pageTitle = `${derivedTitle} ${iconCollection === 'basic3d' ? '3D' : 'vector'} icon`;
  const description = `Beautiful handcrafted ${derivedTitle} ${iconCollection === 'basic3d' ? '3D' : ''}`;

  const getLicenseUrl = () => {
    return iconCollection === 'basic' || iconCollection === 'basic3d'
      ? 'https://creativecommons.org/licenses/by/4.0/'
      : 'https://davooda.com/terms';
  };

  const getContentUrl = () => {
    switch (iconCollection) {
      case 'basic':
        return `https://icons-pbl.davooda.com/basic-icons/${slug}.svg`;
      case 'outline':
        return `https://icons-pbl.davooda.com/outline/png-360px/${slug}-360x360.png`;
      case 'polish-blue':
        return `https://icons-pbl.davooda.com/polish-blue/png-360px/${slug}-360x360.png`;
      case 'basic3d':
        return iconData.url_large || `https://icons-pbl.davooda.com/basic3d-icons/png-512px/${slug}.png`;
      default:
        return imageUrl;
    }
  };

  const getCanonicalUrl = () => {
    switch (iconCollection) {
      case 'basic':
        return `https://davooda.com/basic-icons/${slug}`;
      case 'outline':
        return `https://davooda.com/outline/${slug}`;
      case 'polish-blue':
        return `https://davooda.com/polish-blue-icons/${slug}`;
      case 'basic3d':
        return `https://davooda.com/basic3d/${slug}`;
      default:
        return `https://davooda.com/${slug}`;
    }
  };

  const getImageObjectJSON = () => {
    const firstFour = formatKeywords(assetKeywords, 4);
    const licenseUrl = getLicenseUrl();
    const creditTextPrefix = iconCollection === 'basic' || iconCollection === 'basic3d' ? 'Free' : '';
    const separator = creditTextPrefix ? ' ' : '';
    const iconType = iconCollection === 'basic3d' ? '3D' : 'vector';

    return {
      '@context': 'https://schema.org/',
      '@type': 'ImageObject',
      contentUrl: getContentUrl(),
      license: licenseUrl,
      acquireLicensePage: licenseUrl,
      creditText: `${creditTextPrefix}${separator}${firstFour} ${iconType} icon`.trim(),
      creator: {
        '@type': 'Person',
        name: 'Davooda',
      },
      copyrightNotice: 'Davooda',
    };
  };

  const getBreadcrumbsJSON = () => {
    let secondItemName = '';
    let secondItemLink = '';

    switch (iconCollection) {
      case 'basic':
        secondItemName = 'Basic Icons';
        secondItemLink = 'https://davooda.com/basic-icons/';
        break;
      case 'outline':
        secondItemName = 'Outline Icons';
        secondItemLink = 'https://davooda.com/outline-icons/';
        break;
      case 'polish-blue':
        secondItemName = 'Polish Blue Icons';
        secondItemLink = 'https://davooda.com/polish-blue-icons/';
        break;
      case 'basic3d':
        secondItemName = 'Basic 3D Icons';
        secondItemLink = 'https://davooda.com/basic3d/';
        break;
      default:
        secondItemName = 'All Icons';
        secondItemLink = 'https://davooda.com/';
    }

    return {
      '@context': 'https://schema.org',
      '@type': 'BreadcrumbList',
      itemListElement: [
        {
          '@type': 'ListItem',
          position: 1,
          name: 'Icons',
          item: 'https://davooda.com/',
        },
        {
          '@type': 'ListItem',
          position: 2,
          name: secondItemName,
          item: secondItemLink,
        },
        {
          '@type': 'ListItem',
          position: 3,
          name: slug || 'icon',
          item: `${secondItemLink}${slug || ''}`,
        },
      ],
    };
  };

  // Проверка, является ли иконка типа "rastericon"
  const isRasterIcon = mediaType === 'rastericon';
  
  // Define the image size based on collection - 20% larger for basic3d
  const isBasic3D = iconCollection === 'basic3d';
  const imageSize = isBasic3D ? 307 : 256; // 256 * 1.2 ≈ 307

  // Проверка корзины выполняется во время SSR
  const isLoggedIn = serverAuthState?.isAuthenticated || false;
  const userCartItems = serverAuthState?.user?.cart || [];
  const isInCart = userCartItems.some(item => 
    item.assetId === assetId || item.id === assetId
  );

  return (
    <>
      <Head>
        <title>{pageTitle}</title>
        <meta name="description" content={description} />
        <meta
          name="keywords"
          content={
            Array.isArray(assetKeywords)
              ? assetKeywords.join(', ')
              : assetKeywords
          }
        />
        <meta property="og:title" content={derivedTitle} />
        <meta property="og:description" content={description} />
        <meta property="og:image" content={imageUrl} />
        <meta property="og:type" content="website" />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content={derivedTitle} />
        <meta name="twitter:description" content={description} />
        <meta name="twitter:image" content={imageUrl} />
        <meta name="creator" content="Davooda" />
        <meta name="copyrightnotice" content="Davooda" />
        <link rel="canonical" href={getCanonicalUrl()} />
        <script
          type="application/ld+json"
          dangerouslySetInnerHTML={{
            __html: JSON.stringify(getImageObjectJSON()),
          }}
        />
        <script
          type="application/ld+json"
          dangerouslySetInnerHTML={{
            __html: JSON.stringify(getBreadcrumbsJSON()),
          }}
        />
      </Head>

      <div className="flex flex-wrap pt-8 gap-5 w-full max-w-7xl">
        {/* Icon Preview - Исправленный блок для Safari */}
        <div className="w-full md:w-1/3 flex justify-center items-center">
          <div style={{ width: isBasic3D ? '307px' : '256px', height: isBasic3D ? '307px' : '256px' }} className="flex items-center justify-center">
            {imageUrl && imageUrl.includes('.svg') ? (
              <img
                src={imageUrl}
                alt={derivedTitle}
                className="max-w-full max-h-full object-contain"
                style={{ width: '100%', height: '100%' }}
              />
            ) : (
              <Image
                src={imageUrl || '/placeholder-icon.png'}
                alt={derivedTitle}
                width={imageSize}
                height={imageSize}        
                className="object-contain"
                unoptimized={imageUrl && imageUrl.includes('.svg')}
              />
            )}
          </div>
        </div>

        {/* Icon Details */}
        <div className="flex-1 min-w-[250px] flex flex-col">
          {/* Breadcrumbs */}
          <nav className="mb-1">
            <ul className="list-none p-0 m-0 flex items-center text-xs">
              <li className="flex items-center">
                <Link href="/" className="text-black underline">
                  Icons
                </Link>
                <span className="mx-2">&#62;</span>
              </li>
              <li>
                <Link
                  href={
                    iconCollection === 'basic'
                      ? '/basic-icons/'
                      : iconCollection === 'outline'
                      ? '/outline-icons/'
                      : iconCollection === 'polish-blue'
                      ? '/polish-blue-icons/'
                      : iconCollection === 'basic3d'
                      ? '/basic3d/'
                      : '/'
                  }
                  className="text-black underline"
                >
                  {iconCollection === 'basic3d' ? 'Basic 3D' : iconCollection} Collection
                </Link>
              </li>
            </ul>
          </nav>

          {/* Title and License */}
          <div className="flex flex-col gap-1">
            <h1 className="text-3xl font-bold m-0 text-[#191919] -tracking-[1px] leading-tight">
              {derivedTitle}
            </h1>
            <p className="text-sm leading-7 py-4">
              <b>License:</b>{' '}
              {iconCollection === 'basic' || iconCollection === 'basic3d' ? (
                <a
                  href="https://creativecommons.org/licenses/by/4.0/"
                  className="underline"
                >
                  Creative Commons Attribution 4.0
                </a>
              ) : (
                'Free PNG with attribution, Premium SVG.'
              )}
              <br />
              {!hasAccess && (
                <>
                  <b>Attribution:</b> Required{' '}
                  <Link href="/how-to-attribute" className="underline">
                    How to attribute?
                  </Link>
                </>
              )}
            </p>
          </div>

          {/* Auth error notice */}
          {authError && (
            <div className="mt-3 p-3 bg-yellow-50 border border-yellow-200 rounded-md text-sm">
              <p className="font-medium text-yellow-800">
                {serverErrorMessage ||
                  'Server response: Authentication error. Please log in again to access premium icons.'}
              </p>
            </div>
          )}

          {/* Button section */}
          <div className="flex flex-row flex-wrap items-stretch w-full mb-5 mt-4 gap-4 md:gap-3">
            {isRasterIcon ? (
              /* Упрощенный интерфейс для rastericon */
              <div className="w-full max-w-[300px]">
                {/* Download Free PNG Button */}
                <div className="mb-4">
                  <ClientOnly placeholder={<DownloadFreePNGPlaceholder />}>
                    <DownloadFreePNG
                      slug={slug}
                      collection={iconCollection}
                      url={iconData.url_large}
                      onBeforeAction={onBeforeDownload}
                      onActionComplete={onPNGDownloadComplete}
                    />
                  </ClientOnly>
                </div>
              </div>
            ) : (
              /* Стандартный интерфейс для других типов */
              <>
                <div className="w-full md:w-[300px]">
                  {/* PNG Download Button */}
                  <div className="mb-3 md:mb-1">
                    <ClientOnly placeholder={<PNGButtonPlaceholder />}>
                      <DownloadPNGButton
                        assets={[
                          {
                            slug,
                            collection: iconCollection,
                            media_type: mediaType,
                            is_free: iconCollection === 'basic' || iconCollection === 'basic3d',
                            format: ['png'],
                          },
                        ]}
                        archiveName={`${slug}.zip`}
                        is_purchased={is_purchased}
                        isSubscriptionActive={is_subscription_active}
                        onBeforeAction={onBeforeDownload}
                        onActionComplete={onPNGDownloadComplete}
                      />
                    </ClientOnly>
                  </div>
                </div>

                <div className="w-full md:w-[300px] md:mr-4">
                  {/* SVG Button */}
                  <div className="mb-1">
                    <div className="relative min-h-[55px] w-full">
                      {(iconCollection === 'basic' || is_purchased || is_subscription_active) ? (
                        /* SVG Download Button */
                        <ClientOnly placeholder={<SVGButtonPlaceholder />}>
                          <DownloadSVGButton
                            assets={[
                              {
                                slug,
                                collection: iconCollection,
                                media_type: mediaType,
                                is_free: iconCollection === 'basic',
                                format: ['svg'],
                              },
                            ]}
                            archiveName={`${slug}.svg`}
                            is_purchased={is_purchased}
                            isSubscriptionActive={is_subscription_active}
                            onBeforeAction={onBeforeDownload}
                            onActionComplete={onSVGDownloadComplete}
                          />
                        </ClientOnly>
                      ) : (
                        /* Add To Cart Button */
                        <ClientOnly placeholder={<AddToCartButtonPlaceholder price={price} />}>
                          <AddToCartButton
                            isInCart={isInCart}
                            slug={slug}
                            price={price}
                            collection={iconCollection}
                            media_type={mediaType}
                            onAddToCart={handleAddToCart}
                          />
                        </ClientOnly>
                      )}
                    </div>
                  </div>
                </div>

                {/* Consider Subscription Link */}
                {iconCollection !== 'basic' && !is_purchased && !is_subscription_active && (
                  <div className="w-full md:w-auto flex items-center">
                    <Link 
                      href="/pricing" 
                      className="text-base font-medium underline transition-colors"
                      style={{ color: 'rgb(23, 23, 23)' }}
                    >
                      Or Consider Subscription
                    </Link>
                  </div>
                )}
              </>
            )}
          </div>
        </div>
      </div>

      {/* Similar Icons */}
      {similarIconsData && similarIconsData.items && similarIconsData.items.length > 0 && (
        <SimilarIcons
          slug={slug || ''}
          BACKEND_URL={BACKEND_URL || ''}
          collections={similarCollections || ['basic', 'outline', 'polish-blue', 'basic3d']}
          initialData={similarIconsData || { items: [] }}
        />
      )}
    </>
  );
};

export default IconDetail;